import React from 'react';
import { Megaphone, X } from 'lucide-react';
import { ResultsFilter } from '../../../types/results.types';

interface ActiveFilterPillsProps {
  filters: ResultsFilter;
  onRemoveFilter: (filterKey: keyof ResultsFilter) => void;
  onClearAll: () => void;
}

const ActiveFilterPills: React.FC<ActiveFilterPillsProps> = ({
  filters,
  onRemoveFilter,
  onClearAll,
}) => {
  const getFilterLabel = (key: keyof ResultsFilter, value: any): string => {
    switch (key) {
      case 'searchTerm':
        return `Name: ${value}`;
      case 'minRating':
        return `Min Rating: ${value}`;
      case 'maxRating':
        return `Max Rating: ${value}`;
      case 'minReviews':
        return `Min Reviews: ${value}`;
      case 'maxReviews':
        return `Max Reviews: ${value}`;
      case 'hasWebsite':
        return `Website: ${value ? 'Yes' : 'No'}`;
      case 'hasPhone':
        return `Phone: ${value ? 'Yes' : 'No'}`;
      case 'hasBookVisit':
        return `Booking: ${value ? 'Yes' : 'No'}`;
      case 'hasDecisionMakers':
        return `Decision Makers: ${value ? 'Yes' : 'No'}`;
      case 'hasEmail':
        return `Has Email: ${value ? 'Yes' : 'No'}`;
      case 'emailVerificationStatus':
        const statuses = Array.isArray(value) ? value : [];
        if (statuses.length === 0) return '';
        const statusLabels: Record<string, string> = {
          'safe': 'Verified',
          'likely': 'Likely',
          'unconfirmed': 'Unconfirmed'
        };
        const labels = statuses.map((s: string) => statusLabels[s] || s).join(', ');
        return `Email Status: ${labels}`;
      case 'industry':
        const industries = value.split(',').filter((i: string) => i);
        return industries.length > 1 
          ? `Industries: ${industries.length} selected` 
          : `Industry: ${industries[0]}`;
      case 'strongMatchesOnly':
        return 'Matched businesses';
      case 'matchedOffering':
        return `Matched service: ${value}`;
      case 'googleAdsSignal':
        return value === 'no_ad_tracking' ? 'Google Ads: No signal' : 'Google Ads: Detected recently';
      default:
        return '';
    }
  };

  const activeFilters: Array<{ key: keyof ResultsFilter; value: any }> = [];

  (Object.keys(filters) as Array<keyof ResultsFilter>).forEach((key) => {
    const value = filters[key];
    if (key === 'industry' || key === 'emailVerificationStatus') {
      return;
    }
    if (value !== undefined && value !== null && value !== '') {
      activeFilters.push({ key, value });
    }
  });

  if (filters.industry && filters.industry.split(',').filter(i => i).length > 0) {
    activeFilters.push({ key: 'industry', value: filters.industry });
  }

  if (filters.emailVerificationStatus && Array.isArray(filters.emailVerificationStatus) && filters.emailVerificationStatus.length > 0) {
    activeFilters.push({ key: 'emailVerificationStatus', value: filters.emailVerificationStatus });
  }

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-700">Active Filters:</span>
        <button
          onClick={onClearAll}
          className="text-xs text-primary hover:text-primary-dark font-medium"
        >
          Clear All
        </button>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {activeFilters.map(({ key, value }) => (
          <div
            key={key}
            className="inline-flex items-center px-3 py-1 bg-white border border-gray-300 rounded-full text-xs font-medium text-gray-700 shadow-sm"
          >
            {key === 'googleAdsSignal' && (
              <Megaphone className="mr-1.5 h-3.5 w-3.5 text-[#2B6CB0]" aria-hidden="true" />
            )}
            <span>{getFilterLabel(key, value)}</span>
            <button
              onClick={() => onRemoveFilter(key)}
              className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label={`Remove ${key} filter`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActiveFilterPills;
